import styles from './DefaultHero.module.scss';
import React from 'react';
import classNames from 'classnames';
import {
  Button,
  Content,
  FontAwesomeProIcon,
  getHeadingTag,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Video,
} from '@wienenergiegithub/ui-next';
import PropTypes from 'prop-types';

const DefaultHero = ({
  fitContain,
  handleClick,
  headingLevel = 1,
  hintText,
  image,
  interactionId,
  link,
  list,
  listVariant,
  secondaryContent,
  secondaryInteractionId,
  secondaryLink,
  subtitle,
  title,
  video,
}) => (
  <div
    className={classNames(styles.container, {
      [styles.contentOnly]: !image && !video?.video,
    })}
  >
    <div className={styles.contentContainer}>
      {!!title && (
        <Heading
          as={getHeadingTag(headingLevel)}
          className={classNames(styles.heading, {
            [styles.withHintText]: hintText,
          })}
          size="h1"
        >
          {title}
        </Heading>
      )}
      {!!hintText && (
        <Content className={styles.hintText} noSpacing>
          {hintText}
        </Content>
      )}
      {!!subtitle && (
        <Content as="div" size="md" withParser>
          {subtitle}
        </Content>
      )}
      {list?.length > 0 && (
        <List
          className={styles.list}
          icon={
            <FontAwesomeProIcon
              icon="check"
              role="presentation"
              type="regular"
            />
          }
          type="checkmark"
          variant={listVariant}
        >
          {list.map(({ id, text }) => (
            <ListItem key={id}>{text}</ListItem>
          ))}
        </List>
      )}
      {!!secondaryContent && (
        <Content as="div" size="md" withParser>
          {secondaryContent}
        </Content>
      )}
      {!!link && (
        <div className={styles.buttonContainer}>
          <Button
            isElevated
            onClick={handleClick(interactionId)}
            render={<Link target={link?.target} to={link.url} />}
          >
            {link.title}
          </Button>
          {!!secondaryLink && (
            <Button
              isElevated
              onClick={handleClick(secondaryInteractionId)}
              render={
                <Link target={secondaryLink?.target} to={secondaryLink.url} />
              }
              theme="secondary"
            >
              {secondaryLink.title}
            </Button>
          )}
        </div>
      )}
    </div>
    {!!image && (
      <Image
        alt={image.title}
        className={classNames(styles.media, {
          [styles.fitContain]: fitContain,
        })}
        height={image.height}
        href={link?.url}
        sizes={image.sizes}
        src={image.src}
        srcSet={image.srcSet}
        style={{ aspectRatio: `${image.width} / ${image.height}` }}
        target={link?.target}
        width={image.width}
      />
    )}
    {!!video?.video && (
      <Video
        autoPlay={video.autoplay_enabled}
        className={styles.media}
        controls={video.show_controls}
        loop={video.loop_enabled}
        muted={video.is_muted}
        poster={video.poster}
        preload="none"
        style={{
          aspectRatio: `${video.video.width} / ${video.video.height}`,
        }}
        title={video.title}
        track={video.track}
        video={video.video}
      />
    )}
  </div>
);

DefaultHero.propTypes = {
  fitContain: PropTypes.bool,
  handleClick: PropTypes.func,
  headingLevel: PropTypes.number,
  hintText: PropTypes.string,
  image: PropTypes.shape({
    height: PropTypes.number,
    sizes: PropTypes.string,
    src: PropTypes.string.isRequired,
    srcSet: PropTypes.string,
    title: PropTypes.string,
    width: PropTypes.number,
  }),
  interactionId: PropTypes.string,
  link: PropTypes.shape({
    target: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  list: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, text: PropTypes.string }),
  ),
  listVariant: PropTypes.string,
  secondaryContent: PropTypes.string,
  secondaryInteractionId: PropTypes.string,
  secondaryLink: PropTypes.shape({
    target: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  video: PropTypes.shape({
    autoplay_enabled: PropTypes.bool,
    src: PropTypes.string,
    srcSet: PropTypes.string,
    is_muted: PropTypes.bool,
    loop_enabled: PropTypes.bool,
    poster: PropTypes.string,
    show_controls: PropTypes.bool,
    title: PropTypes.string,
    track: PropTypes.string,
    video: PropTypes.shape({
      height: PropTypes.number,
      name: PropTypes.string,
      src: PropTypes.string,
      width: PropTypes.number,
    }),
  }),
};

export default DefaultHero;
